import type { MercureUpdate } from './MercureUpdate';

export type SubscriptionId = string & { readonly __brand: 'SubscriptionId' };

export function createSubscriptionId (value: string): SubscriptionId {
  if (!value || value.trim().length === 0) {
    throw new Error('Subscription id cannot be empty');
  }
  return value.trim() as SubscriptionId;
}

export function generateSubscriptionId (): SubscriptionId {
  const timestamp: string = Date.now().toString(36);
  const random: string = Math.random().toString(36).substring(2, 10);
  return createSubscriptionId(`sub_${timestamp}_${random}`);
}

export type UpdateHandler<T = unknown> = (update: MercureUpdate<T>) => void | Promise<void>;

export type SubscriptionStatus =
  | { readonly state: 'pending' }
  | { readonly state: 'active'; readonly since: Date }
  | { readonly state: 'cancelled'; readonly at: Date }
  | { readonly state: 'failed'; readonly error: Error; readonly at: Date };

export interface Subscription<T = unknown> {
  readonly id: SubscriptionId;
  readonly topics: readonly string[];
  readonly handler: UpdateHandler<T>;
  readonly status: SubscriptionStatus;
  readonly createdAt: Date;
  readonly updateCount: number;
}

export function createSubscription<T = unknown> (params: {
  topics: string[];
  handler: UpdateHandler<T>;
  id?: SubscriptionId;
}): Subscription<T> {
  return {
    id: params.id ?? generateSubscriptionId(),
    topics: params.topics,
    handler: params.handler,
    status: { state: 'pending' },
    createdAt: new Date(),
    updateCount: 0,
  };
}

export function markAsActive<T> (subscription: Subscription<T>): Subscription<T> {
  return {
    ...subscription,
    status: { state: 'active', since: new Date() },
  };
}

export function incrementUpdateCount<T> (subscription: Subscription<T>): Subscription<T> {
  return {
    ...subscription,
    updateCount: subscription.updateCount + 1,
  };
}

export function isActive (subscription: Subscription): boolean {
  return subscription.status.state === 'active';
}
